import React, { useEffect, useState } from 'react';
import { Spin, Result, Button } from 'antd';
import { useIntl } from 'react-intl';

function parseTenantFromToken(accessToken: string): string | null {
  try {
    const payload = accessToken.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.tid || null;
  } catch {
    return null;
  }
}

const SharePointCallback = () => {
  const intl = useIntl();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const errorParam = params.get('error');
      const errorDescription = params.get('error_description');

      if (errorParam) {
        setError(errorDescription || errorParam);
        return;
      }

      // Admin consent callback: returns admin_consent=True&tenant=...
      const adminConsent = params.get('admin_consent');
      if (adminConsent === 'True') {
        const tenant = params.get('tenant');
        console.log('[SharePoint] Admin consent granted for tenant:', tenant);
        sessionStorage.setItem('sp_admin_consent', 'true');
        if (tenant) {
          sessionStorage.setItem('sp_tenant_id', tenant);
        }
        sessionStorage.removeItem('sp_oauth_state');
        window.location.href = '/setup/sharepoint';
        return;
      }

      // PKCE auth code callback
      const code = params.get('code');
      const returnedState = params.get('state');

      if (!code) {
        setError('No authorization code received from Azure AD.');
        return;
      }

      const savedState = sessionStorage.getItem('sp_oauth_state');
      if (returnedState !== savedState) {
        setError('OAuth state mismatch. Possible CSRF attack.');
        return;
      }

      const codeVerifier = sessionStorage.getItem('sp_code_verifier');
      const clientId = sessionStorage.getItem('sp_client_id');

      if (!codeVerifier || !clientId) {
        setError('Missing PKCE parameters. Please restart the setup flow.');
        return;
      }

      const redirectUri = `${window.location.origin}/sharepoint-callback`;

      try {
        const response = await fetch(
          `https://login.microsoftonline.com/organizations/oauth2/v2.0/token`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              client_id: clientId,
              grant_type: 'authorization_code',
              code,
              redirect_uri: redirectUri,
              code_verifier: codeVerifier,
              scope:
                'https://graph.microsoft.com/Sites.FullControl.All https://graph.microsoft.com/Sites.Read.All offline_access',
            }),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          setError(
            data.error_description || data.error || 'Token exchange failed.'
          );
          return;
        }

        console.log('[SharePoint] Token received:', data);
        sessionStorage.setItem('sp_access_token', data.access_token);
        if (data.refresh_token) {
          sessionStorage.setItem('sp_refresh_token', data.refresh_token);
        }

        // Extract tenant ID from the JWT token
        const tenantId = parseTenantFromToken(data.access_token);
        if (tenantId) {
          sessionStorage.setItem('sp_tenant_id', tenantId);
          console.log('[SharePoint] Tenant ID:', tenantId);
        }

        // Clean up PKCE artifacts
        sessionStorage.removeItem('sp_code_verifier');
        sessionStorage.removeItem('sp_oauth_state');
        sessionStorage.removeItem('sp_flow');

        window.location.href = '/setup/sharepoint?step=1';
      } catch (err: any) {
        console.error('[SharePoint] Token exchange error:', err);
        setError(err.message || 'Failed to exchange authorization code.');
      }
    };

    handleCallback();
  }, []);

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Result
          status="error"
          title={intl.formatMessage({
            id: 'fleetadmin.sharepoint.callback.error.title',
          })}
          subTitle={error}
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.href = '/setup/sharepoint';
              }}
            >
              {intl.formatMessage({
                id: 'fleetadmin.sharepoint.callback.backToSetup',
              })}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <Spin size="large" />
      <p>
        {intl.formatMessage({ id: 'fleetadmin.sharepoint.callback.loading' })}
      </p>
    </div>
  );
};

export default SharePointCallback;
